import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Polyline, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Locate, Navigation, Phone, CheckCircle2, Store as StoreIcon } from "lucide-react";

const mk = (bg, emoji, anchorBottom = false) => L.divIcon({
  className: "",
  html: `<div style="background:${bg};color:#fff;width:36px;height:36px;border-radius:50%;display:grid;place-items:center;border:3px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.3);font-size:17px;">${emoji}</div>`,
  iconSize: [36, 36],
  iconAnchor: anchorBottom ? [18, 34] : [18, 18],
});
const ICON_DRIVER = mk("#E32636", "🛵");
const ICON_RESTO = mk("#111111", "🍴", true);
const ICON_CLIENT = mk("#009E60", "📍", true);
const CONAKRY = [9.6412, -13.5784];

const toKm = (a, b) => {
  const R = 6371, dLat = (b[0] - a[0]) * Math.PI / 180, dLng = (b[1] - a[1]) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);
const etaMin = (km) => Math.max(2, Math.round((km / 22) * 60)); // scooter ~22 km/h

function FitTo({ points, recenterTick }) {
  const map = useMap();
  useEffect(() => {
    const pts = points.filter(Boolean);
    if (!pts.length) return;
    if (pts.length === 1) map.setView(pts[0], 15, { animate: true });
    else map.fitBounds(L.latLngBounds(pts), { padding: [46, 46], maxZoom: 16 });
  }, [JSON.stringify(points), recenterTick, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const phaseKey = (id) => `neldaari_mission_phase_${id}`;

/**
 * Écran « Courses » basé carte (états 1→6) : carte plein écran + bottom sheet.
 * Réutilise les statuts existants (ready → delivering → delivered/code PIN) ;
 * les sous-étapes (arrivé resto, commande récupérée, arrivé client) sont locales.
 */
export default function DriverMissionMap({ orders, online, lastPos, onAccept, onDecline, onDelivered, onIncident, onArrivedResto }) {
  const { t } = useTranslation();
  const [recenterTick, setRecenterTick] = useState(0);
  const [, forceRender] = useState(0);
  const [zones, setZones] = useState([]);

  const active = orders.find((o) => o.status === "delivering");
  // Les offres prioritaires ont leur propre overlay dédié : le sheet ne montre que le pool ouvert
  const offers = orders.filter((o) => o.status === "ready" && !o.blocked_by_debt && !o.is_priority_offer);
  const offer = !active ? offers[0] : null;

  // Zones de demande (état 1 : pas de course) — rafraîchies toutes les 2 min
  useEffect(() => {
    if (active || offer) { setZones([]); return undefined; }
    let stop = false;
    const load = async () => {
      try {
        const { apiClient } = await import("@/lib/api");
        const { data } = await apiClient.get("/driver/demand-zones");
        if (!stop) setZones(data.zones || []);
      } catch { /* zones facultatives */ }
    };
    load();
    const id = setInterval(load, 120000);
    return () => { stop = true; clearInterval(id); };
  }, [!!active, !!offer]); // eslint-disable-line react-hooks/exhaustive-deps

  const phase = active ? (localStorage.getItem(phaseKey(active.order_id)) || "to_resto") : null;
  const setPhase = (p) => { if (active) { localStorage.setItem(phaseKey(active.order_id), p); forceRender((x) => x + 1); } };

  const me = lastPos ? [lastPos.lat, lastPos.lng] : null;
  const focus = active || offer;
  const resto = focus?.restaurant_gps?.lat ? [focus.restaurant_gps.lat, focus.restaurant_gps.lng] : null;
  const client = focus?.client_gps?.lat ? [focus.client_gps.lat, focus.client_gps.lng] : null;

  // Cible selon l'état : offre/direction resto → resto ; récupérée → client
  const toClient = active && (phase === "to_client" || phase === "at_client");
  const target = toClient ? client : resto;
  const line = me && target ? [me, target] : null;
  const km = line ? toKm(me, target) : null;
  const fitPoints = useMemo(() => (focus ? [me, target].filter(Boolean) : [me].filter(Boolean)), [me, target, focus]);

  const gmaps = (dest, addr) => dest
    ? `https://www.google.com/maps/dir/?api=1&destination=${dest[0]},${dest[1]}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((addr || "") + ", Conakry")}`;

  const short = focus ? `#${(focus.order_id || "").slice(-6).toUpperCase()}` : "";
  const gain = focus?.delivery_fee ? Math.round(focus.delivery_fee * 0.7) : null;

  return (
    <div className="rounded-2xl overflow-hidden border border-sand bg-white" data-testid="driver-mission-map">
      {/* ===== CARTE (~62vh) ===== */}
      <div className="relative" style={{ height: "62vh", minHeight: 380 }}>
        <MapContainer center={me || CONAKRY} zoom={14} style={{ height: "100%", width: "100%" }} zoomControl={false} attributionControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitTo points={fitPoints} recenterTick={recenterTick} />
          {me && <Marker position={me} icon={ICON_DRIVER} />}
          {!focus && zones.map((z, i) => (
            <Circle
              key={i}
              center={[z.lat, z.lng]}
              radius={Math.min(900, 350 + z.count * 120)}
              pathOptions={{ color: "#E32636", weight: 1, fillColor: "#E32636", fillOpacity: Math.min(0.35, 0.10 + z.count * 0.05) }}
            />
          ))}
          {focus && resto && !toClient && <Marker position={resto} icon={ICON_RESTO} />}
          {focus && client && (toClient || !resto) && <Marker position={client} icon={ICON_CLIENT} />}
          {line && <Polyline positions={line} pathOptions={{ color: "#E32636", weight: 4, dashArray: "8 10" }} />}
        </MapContainer>
        {/* Recentrer */}
        <button
          onClick={() => setRecenterTick((x) => x + 1)}
          data-testid="mission-recenter"
          aria-label={t("ddrv.mmRecenter")}
          className="absolute bottom-4 right-3 z-[500] rounded-full bg-white p-3 shadow-lg border border-sand text-ink active:scale-95"
        >
          <Locate className="w-5 h-5" />
        </button>
        {/* Bandeau direction */}
        {active && (
          <div className="absolute top-3 right-3 z-[500] rounded-full bg-white/95 px-3.5 py-2 text-xs font-bold shadow-lg border border-sand" data-testid="mission-direction">
            {toClient ? t("ddrv.mmDirClient") : t("ddrv.mmDirResto")}
          </div>
        )}
      </div>

      {/* ===== BOTTOM SHEET ===== */}
      <div className="relative -mt-5 z-[600] mx-2 mb-2 rounded-2xl bg-white shadow-[0_-6px_30px_rgba(0,0,0,0.12)] border border-sand p-4" data-testid="mission-sheet">
        <div className="w-10 h-1 rounded-full bg-sand mx-auto mb-3" />

        {/* ÉTAT 1 — pas de course */}
        {!focus && (
          <div className="text-center py-2">
            <div className={`font-display font-bold text-base ${online ? "text-brand-green" : "text-ink"}`}>
              {online ? t("ddrv.mmOnlineTitle") : t("ddrv.mmOfflineTitle")}
            </div>
            <p className="text-xs text-mute mt-1">{online ? t("ddrv.mmOnlineHint") : t("ddrv.mmOfflineHint")}</p>
          </div>
        )}

        {/* ÉTAT 2 — nouvelle course proposée */}
        {offer && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold text-base flex items-center gap-2"><StoreIcon className="w-4 h-4 text-brand-red" /> {offer.restaurant_name}</div>
                <p className="text-xs text-mute mt-0.5">{offer.restaurant_address || ""}</p>
                <p className="text-xs text-mute mt-0.5">{t("ddrv.mmDeliverTo")} : {offer.address}</p>
              </div>
              {gain != null && <div className="shrink-0 rounded-xl bg-brand-green/10 text-brand-green px-3 py-1.5 text-sm font-bold">+{gain.toLocaleString("fr-FR")} GNF</div>}
            </div>
            {km != null && (
              <div className="flex gap-3 text-xs text-mute">
                <span>📍 {fmtKm(km)} {t("ddrv.mmToResto")}</span>
                <span>⏱ ~{etaMin(km)} min</span>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => onDecline(offer)} data-testid="mission-decline" className="flex-1 rounded-full border border-sand py-3 text-sm font-semibold text-ink">
                {t("ddrv.mmDecline")}
              </button>
              <button onClick={() => onAccept(offer)} data-testid="mission-accept" className="flex-[2] rounded-full bg-brand-red text-white py-3 text-sm font-bold active:scale-[0.98]">
                {t("ddrv.mmAccept")}
              </button>
            </div>
          </div>
        )}

        {/* ÉTATS 3→6 — course active */}
        {active && (
          <div className="space-y-3">
            {/* Progression des étapes de la mission */}
            <div className="flex items-center gap-1.5" data-testid="mission-stepper">
              {["to_resto", "at_resto", "to_client", "at_client"].map((p, i) => {
                const idx = ["to_resto", "at_resto", "to_client", "at_client"].indexOf(phase);
                return <div key={p} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= idx ? "bg-brand-red" : "bg-sand"}`} />;
              })}
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold text-base">
                  {phase === "to_resto" && t("ddrv.mmDirResto")}
                  {phase === "at_resto" && t("ddrv.mmPickupTitle")}
                  {phase === "to_client" && t("ddrv.mmDeliveringTitle")}
                  {phase === "at_client" && t("ddrv.mmArrivedTitle")}
                </div>
                <p className="text-xs text-mute mt-0.5">
                  {toClient ? active.address : `${active.restaurant_name} · ${active.restaurant_address || ""}`}
                </p>
                <p className="text-[11px] text-mute mt-0.5">{t("ddrv.mmOrder")} {short}{phase !== "to_resto" && active.client_name ? ` · ${active.client_name}` : ""}</p>
              </div>
              {km != null && phase !== "at_client" && (
                <div className="shrink-0 text-right">
                  <div className="font-display font-bold text-sm">{fmtKm(km)}</div>
                  <div className="text-[11px] text-mute">~{etaMin(km)} min</div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <a
                href={gmaps(toClient ? client : resto, toClient ? active.address : active.restaurant_address || active.restaurant_name)}
                target="_blank" rel="noreferrer" data-testid="mission-route"
                className="rounded-full border border-sand px-4 py-3 text-sm font-semibold inline-flex items-center gap-1.5 text-ink"
              >
                <Navigation className="w-4 h-4" /> {t("ddrv.mmRoute")}
              </a>
              {toClient && active.client_phone && (
                <a href={`tel:${active.client_phone}`} data-testid="mission-call" className="rounded-full border border-sand px-4 py-3 text-sm inline-flex items-center text-ink"><Phone className="w-4 h-4" /></a>
              )}
              {phase === "to_resto" && (
                <button onClick={() => { setPhase("at_resto"); onArrivedResto?.(active); }} data-testid="mission-arrived-resto" className="flex-1 rounded-full bg-ink text-white py-3 text-sm font-bold">
                  {t("ddrv.mmArrivedResto")}
                </button>
              )}
              {phase === "at_resto" && (
                <button onClick={() => setPhase("to_client")} data-testid="mission-picked-up" className="flex-1 rounded-full bg-brand-red text-white py-3 text-sm font-bold">
                  {t("ddrv.mmPickedUp")}
                </button>
              )}
              {phase === "to_client" && (
                <button onClick={() => setPhase("at_client")} data-testid="mission-arrived-client" className="flex-1 rounded-full bg-ink text-white py-3 text-sm font-bold">
                  {t("ddrv.mmArrivedClient")}
                </button>
              )}
              {phase === "at_client" && (
                <button onClick={() => onDelivered(active)} data-testid="mission-delivered" className="flex-1 rounded-full bg-brand-green text-white py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {t("ddrv.mmDelivered")}
                </button>
              )}
            </div>
            {onIncident && (
              <button
                onClick={() => onIncident(active.order_id)}
                data-testid="mission-incident"
                className="w-full text-center text-xs font-semibold text-brand-red/80 hover:text-brand-red py-1"
              >
                ⚠ {t("ddrv.btnIncident")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
